export interface PipeSize {
  size:   number;
  wallThickness: number;
  buckleArrestors: number;
  numberOfPipes: number;
  pipeLength: number;
  numberOfForges: number;
  outputAssemblyLength: number;
  manufacturer: string;
}
export interface PipeUnit {
  pipeSize?: string;
  wallThickness?: string;
  length?: string;
  outputAssemblyLength?: string;
}

/** Optional per-spool row for fabrication “spools” setup (saved on the pipe as `spoolDetails`). */
export interface SpoolSetupDetailRow {
  spoolName?: string;
  spoolNo?: string;
  spoolType?: string;
  spoolLength?: string;
  /** How spool length is interpreted for this row (geometry / completion rules); e.g. N/A, Metric, Imperial, or project-specific labels. Persisted in `spoolDetails` JSON. */
  measureType?: string;
  welds?: string;
}

/** Backend / save shape for spools on a pipe (same for load and save). */
export interface SpoolDetailsPayload {
  numberOfSpools: number;
  spoolDetails: SpoolSetupDetailRow[];
}

export function buildSpoolDetailsPayload(
  count: number,
  rows: SpoolSetupDetailRow[]
): SpoolDetailsPayload {
  const n = Math.max(0, Number(count) || 0);
  return { numberOfSpools: n, spoolDetails: rows };
}

export function getSpoolDetailsFromPipe(pipe: PipeSize1): SpoolSetupDetailRow[] {
  const top = pipe.spoolDetails as unknown;
  if (Array.isArray(top)) return top as SpoolSetupDetailRow[];
  // API / save shape: { numberOfSpools?: number; spoolDetails: SpoolSetupDetailRow[] }
  if (top && typeof top === 'object' && !Array.isArray(top)) {
    const inner = (top as Record<string, unknown>).spoolDetails;
    if (Array.isArray(inner)) return inner as SpoolSetupDetailRow[];
  }
  return [];
}

/**
 * Spool / buckle-arrestor production count from a pipe row.
 * Prefer `numberOfSpools` when present; else `number_of_spools` (API tag); else legacy `numberOfBuckleArrestors`.
 */
export function getNumberOfSpoolsFromPipe(
  pipe: Partial<PipeSize1> | Record<string, unknown>
): number {
  const p = pipe as Record<string, unknown>;
  const has = (v: unknown) => v !== undefined && v !== null && v !== '';
  if (has(p.numberOfSpools)) return Number(p.numberOfSpools) || 0;
  if (has(p.number_of_spools)) return Number(p.number_of_spools) || 0;

  const sd = p.spoolDetails as unknown;
  if (sd && typeof sd === 'object' && !Array.isArray(sd)) {
    const rec = sd as Record<string, unknown>;
    if (has(rec.numberOfSpools)) return Number(rec.numberOfSpools) || 0;
  }

  if (has(p.numberOfBuckleArrestors)) return Number(p.numberOfBuckleArrestors) || 0;

  if (sd && typeof sd === 'object' && !Array.isArray(sd)) {
    const inner = (sd as Record<string, unknown>).spoolDetails;
    if (Array.isArray(inner)) return inner.length;
  }
  if (Array.isArray(sd)) return sd.length;

  return 0;
}

/** Keep legacy API field and new spools field identical on a pipe row. */
export function withSyncedSpoolCounts<T extends Partial<PipeSize1>>(
  pipe: T,
  count: number
): T & { numberOfSpools: number; numberOfBuckleArrestors: number } {
  const n = Number(count) || 0;
  return { ...pipe, numberOfSpools: n, numberOfBuckleArrestors: n };
}

/** Default spool list name when none is saved (`SPOOL-1`, `SPOOL-2`, …). */
export function defaultSpoolDisplayName(indexZeroBased: number): string {
  return `SPOOL-${indexZeroBased + 1}`;
}

/** Trim / pad `spoolDetails` to match `numberOfSpools` while preserving existing row values. */
export function normalizeSpoolDetailsRows(
  count: number,
  existing: SpoolSetupDetailRow[] | undefined | null
): SpoolSetupDetailRow[] {
  const prev = Array.isArray(existing) ? existing : [];
  return Array.from({ length: Math.max(0, count) }, (_, i) => {
    const raw = prev[i]?.spoolName;
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    const mt =
      typeof prev[i]?.measureType === 'string' && prev[i]!.measureType!.trim() !== ''
        ? prev[i]!.measureType!.trim()
        : 'N/A';
    return {
      spoolName: trimmed !== '' ? trimmed : defaultSpoolDisplayName(i),
      spoolNo: prev[i]?.spoolNo ?? '',
      spoolType: prev[i]?.spoolType ?? '',
      spoolLength: prev[i]?.spoolLength ?? '',
      measureType: mt,
      welds: prev[i]?.welds ?? '',
    };
  });
}

export interface PipeSize1 {
  pipeId?: number;
  pipeName: string;
  pipeSize: number;
  wallThickness: number;
  /** Spools fabrication count (and clearer name in UI); kept equal to `numberOfBuckleArrestors` for API compatibility. */
  numberOfSpools?: number;
  /** Legacy API / persistence field — same numeric value as `numberOfSpools` (do not remove). */
  numberOfBuckleArrestors?: number;
  numberOfPipes: number;
  pipeLength: number;
  numberOfForges: number;
  outputAssemblyLength: number;
  manufacturerId: number;
  manufacturerIds?: number[];
  pipeOrder: number;
  isActive: boolean;
  prefixAssemblyName?: string;
  jobNumber?:any
  units?: PipeUnit; // API expects 'units'
  /** Spools fabrication: API object `{ numberOfSpools, spoolDetails: rows }`, or legacy bare row array. */
  spoolDetails?: SpoolDetailsPayload | SpoolSetupDetailRow[];
  pipeDetailsExtraDetails?: { isReducerPipe?: boolean } | Record<string, unknown>;
  documentsData: {
    numberOfDocs: number;
    isDocumentsAvailable: boolean;
    documentIds: number[];
    documentFileNames: string[];
  };
  productionData?: {
    numberOfProductions: number;
    isProductionAvailable: boolean;
  };
  materialsData?: {
    numberOfMaterials: number;
    isMaterialsAvailable: boolean;
  };
}

// New comprehensive pipeline setup interfaces
export interface PipelinePipeSize {
  pipeId?: number;
  pipeName: string;
  pipeSize: number;
  wallThickness: number;
  numberOfSpools?: number;
  numberOfBuckleArrestors?: number;
  numberOfPipes: number;
  pipeLength: number;
  numberOfForges: number;
  outputAssemblyLength: number;
  manufacturerId: number;
  manufacturerIds?: number[];
  pipeOrder: number;
  isActive: boolean;
  prefixAssemblyName?: string;
  jobNumber?:any
  pipeDetailsExtraDetails?: { isReducerPipe?: boolean } | Record<string, unknown>;
  documentsData: {
    numberOfDocs: number;
    isDocumentsAvailable: boolean;
    documentIds: number[];
    documentFileNames: string[];
  };
}

export interface PipelineSetupData {
  projectId: number;
  projectTypeId: number;
  projectType: 'pipeline' | 'fabrication' | 'others';
  projectSubType: string;
  numberOfPipeSizes: number;
  pipeSizes: PipelinePipeSize[];
}

export interface FabricationSetupData {
  projectId: number;
  projectTypeId: number;
  projectType: 'pipeline' | 'fabrication' | 'others';
  projectSubType: 'buckleArrestor' | 'pipeInPipe' | 'bulkhead' | 'spools' | 'none';
  numberOfPipeSizes: number;
  pipeSizes: PipelinePipeSize[];
}

export interface ProjectSetupData {
  [key: string]: PipelineSetupData | FabricationSetupData;
}

export interface SetupState {
  numberOfPipeSizes: number;
  pipeSizes: PipeSize[];
  manufacturers: string[];
  isAddingManufacturer: boolean;
  isSaving: boolean;
  newManufacturer: string;
}


export interface SetupState1 {
  numberOfPipeSizes: number;
  projectId: number;
  projectTypeId: number;
  projectType: string;
  projectSubType: string;
  pipeSizes: PipeSize1[];

}
export interface PipelineSetup extends SetupState {
  type: 'pipeline';
}
export interface PipelineSetup1 extends SetupState1 {
  type: 'pipeline';
}

export interface BuckleArrestorSetup extends SetupState {
  type: 'buckleArrestor';
}

export interface PipeInPipeSetup extends SetupState {
  type: 'pipeInPipe';
}

export interface BulkHeadSetup extends SetupState {
  type: 'bulkHead';
}

export interface SpoolsSetup extends SetupState {
  type: 'spools';
} 